/**
 * Pure Kronos helpers — time parsing, form validation, permission
 * mirrors. No React, no Firestore SDK (beyond the Timestamp type which
 * is a data-only class). Exhaustively unit-tested in `__tests__/`.
 *
 * Kept dependency-free so the logic can be reused by Cloud Functions
 * (Phase 8 timesheet locking, Phase 9 computed fields) without
 * bundling React or the client SDK.
 */

import type { TimeEntry, Role } from '@altyor-kronos/shared-types';

// ─── Form shape ─────────────────────────────────────────────────────

export type TimeEntryMode = 'duration' | 'range';

export interface TimeEntryFormValues {
  projectId: string;
  designationId: string;
  description: string;
  /** YYYY-MM-DD (the HTML <input type="date"> format). */
  date: string;
  mode: TimeEntryMode;
  /** HH:mm (the HTML <input type="time"> format). */
  startHHmm: string;
  /** HH:mm in range mode, empty in duration mode. */
  endHHmm: string;
  /** Decimal hours ("1.5") or colon form ("1:30"), in duration mode. */
  durationHours: string;
  tags: string[];
  billable: boolean;
}

export type FormErrors<T> = Partial<Record<keyof T, string>>;

// ─── Parsers ────────────────────────────────────────────────────────

/**
 * Accept three input flavours and return a numeric hours value:
 *   "1.5"  → 1.5
 *   "1,5"  → 1.5   (French decimal comma)
 *   "1:30" → 1.5
 *
 * Returns `null` for empty, garbage, zero, negative, or > 24h. Zero is
 * rejected because an empty time entry has no meaning — forcing the
 * form to error is safer than silently swallowing the submit.
 */
export function parseHoursInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  let hours: number;
  if (trimmed.includes(':')) {
    const parts = trimmed.split(':');
    if (parts.length !== 2) return null;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || m < 0 || m >= 60) return null;
    hours = h + m / 60;
  } else {
    // Accept comma OR dot as decimal separator.
    const normalised = trimmed.replace(',', '.');
    hours = Number(normalised);
    if (!Number.isFinite(hours)) return null;
  }

  if (hours <= 0) return null;
  if (hours > 24) return null;
  return hours;
}

/**
 * Format seconds to "H:MM" with zero-padded minutes. Seconds below a
 * minute are floored, matching how most trackers display accumulated
 * time.
 */
export function formatDurationSeconds(seconds: number): string {
  const totalMinutes = Math.floor(seconds / 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${m.toString().padStart(2, '0')}`;
}

/**
 * Parse "HH:mm" (zero-padded, 00:00 to 23:59) into minutes-of-day.
 * Returns `null` if the format does not match exactly — we do not
 * accept "9:00" or "09:5" because the HTML <input type="time">
 * already normalises to the strict form.
 */
export function parseHHmm(hhmm: string): number | null {
  if (!/^\d{2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (h < 0 || h > 23) return null;
  if (m < 0 || m > 59) return null;
  return h * 60 + m;
}

/**
 * Combine a YYYY-MM-DD date string + HH:mm time string into a local
 * Date. Returns `null` if either part is malformed.
 */
export function combineDateAndTime(dateISO: string, hhmm: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateISO)) return null;
  const minutesOfDay = parseHHmm(hhmm);
  if (minutesOfDay === null) return null;
  const [y, mo, d] = dateISO.split('-').map(Number);
  const date = new Date(y, mo - 1, d, 0, 0, 0, 0);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mo - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  date.setMinutes(minutesOfDay);
  return date;
}

// ─── Bidirectional time converter ───────────────────────────────────

export type BuildEntryTimesInput = Pick<
  TimeEntryFormValues,
  'date' | 'mode' | 'startHHmm' | 'endHHmm' | 'durationHours'
>;

export type BuildEntryTimesResult =
  | { startTime: Date; endTime: Date; durationSeconds: number }
  | { error: string };

/**
 * Single source of truth for (date, mode, start, end, duration) →
 * (startTime, endTime, durationSeconds). Both directions live here so
 * the form can swap mode without duplicating the math and Cloud
 * Functions can reuse the same converter if needed.
 *
 *   mode=duration: endTime = startTime + durationHours
 *   mode=range:    durationSeconds = endTime - startTime
 *
 * Returns a tagged union: success carries the Date pair + the
 * computed duration in seconds (integer), failure carries a short
 * error code so the caller can i18n the message.
 */
export function buildEntryTimes(input: BuildEntryTimesInput): BuildEntryTimesResult {
  const startTime = combineDateAndTime(input.date, input.startHHmm);
  if (!startTime) return { error: 'invalidStart' };

  if (input.mode === 'duration') {
    const hours = parseHoursInput(input.durationHours);
    if (hours === null) return { error: 'invalidDuration' };
    const durationSeconds = Math.round(hours * 3600);
    const endTime = new Date(startTime.getTime() + durationSeconds * 1000);
    return { startTime, endTime, durationSeconds };
  }

  // range mode — support midnight-crossing shifts (e.g. 23:00→01:00).
  const endTime = combineDateAndTime(input.date, input.endHHmm);
  if (!endTime) return { error: 'invalidEnd' };
  // If end is strictly before start, the entry crosses midnight — push
  // endTime to the next calendar day. Equal times remain an error (0 duration).
  if (endTime.getTime() < startTime.getTime()) {
    endTime.setDate(endTime.getDate() + 1);
  }
  const durationSeconds = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
  if (durationSeconds <= 0) return { error: 'endBeforeStart' };
  return { startTime, endTime, durationSeconds };
}

// ─── Form validation ────────────────────────────────────────────────

/**
 * Structural + semantic checks for the tracker form. Presentation is
 * left to the component — callers look up the error key in i18n to
 * render a helpful message. Mirrors the shape of validators in
 * `features/projects/*.ts` (Phase 2+).
 */
export function validateTimeEntryForm(
  v: TimeEntryFormValues,
): FormErrors<TimeEntryFormValues> {
  const errors: FormErrors<TimeEntryFormValues> = {};

  if (!v.projectId || v.projectId.trim() === '') {
    errors.projectId = 'Project is required';
  }
  if (!v.designationId || v.designationId.trim() === '') {
    errors.designationId = 'Designation is required';
  }
  if (!v.date || v.date.trim() === '') {
    errors.date = 'Date is required';
  }
  if (v.description && v.description.length > 500) {
    errors.description = 'Description must be 500 characters or less';
  }

  // Delegate the time math to the converter so a single source of truth
  // governs "is this set of fields sane?". Map the tagged error back onto
  // the right form field.
  const built = buildEntryTimes(v);
  if ('error' in built) {
    switch (built.error) {
      case 'invalidStart':
        errors.startHHmm = 'Invalid start time';
        break;
      case 'invalidDuration':
        errors.durationHours = 'Invalid duration';
        break;
      case 'invalidEnd':
        errors.endHHmm = 'Invalid end time';
        break;
      case 'endBeforeStart':
        // No longer reachable — midnight crossings are now supported.
        // Kept for defensive completeness.
        errors.endHHmm = 'End must be after start';
        break;
    }
  }
  return errors;
}

/**
 * Per-project Kronos tab: does this viewer read the whole team's rows
 * (and therefore need the userId column), or only their own?
 *
 * Mirrors the server's `isPrivileged` — kronos-api `authz/listScopes.ts`
 * scopes a non-privileged caller to `userId == uid` whatever it asks for,
 * so the client must ask for exactly what the server would grant.
 *
 * This tested `roles.includes('rd_manager')` until 2026-09-18. The Entra
 * token only ever carries employee/admin/direction/finance (4-role model,
 * #48), so it returned false for EVERY viewer: the tab requested
 * `?userId=<me>` on every project and showed 0 entries, while Rapports
 * (unscoped) showed the same hours correctly. It stayed invisible until
 * production held 48k entries belonging to other people.
 *
 * Per-project managers are deliberately NOT here: the server has no
 * data-driven branch for them on timeEntries lists, so widening the client
 * alone would print a userId column over rows that are still self-only.
 */
export function shouldShowUserColumn(roles: readonly Role[]): boolean {
  return roles.some((r) => r === 'admin' || r === 'direction' || r === 'finance');
}

// ─── Small list utilities ───────────────────────────────────────────

export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Pure sort helper — never mutates its input. Firestore's `orderBy`
 * already sorts server-side, but we re-sort locally after merging
 * optimistic writes.
 */
export function sortEntriesByStartDesc(entries: readonly TimeEntry[]): TimeEntry[] {
  return [...entries].sort((a, b) => b.startTime.toMillis() - a.startTime.toMillis());
}

export function sumDurationSeconds(entries: readonly TimeEntry[]): number {
  return entries.reduce((total, e) => total + (e.durationSeconds ?? 0), 0);
}

// ─── HTML input formatters ──────────────────────────────────────────

export function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

export function toTimeInput(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Short localized format for a TimeEntry's start time. Combines the
 * day/month abbreviation with HH:MM — compact enough for a row cell
 * but unambiguous enough to orient the reader ("was that yesterday
 * or last week?"). Examples:
 *   fr-FR → "13 avr. 09:00"
 *   en-US → "Apr 13, 09:00"
 *
 * Intl.DateTimeFormat is used instead of hand-rolled strings so the
 * locale the i18n stack selects also drives the date format.
 */
export function formatEntryDateTime(date: Date, locale: string): string {
  const day = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
  }).format(date);
  const time = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
  return `${day} · ${time}`;
}

// ─── Recap inline-edit helpers ──────────────────────────────────────

/**
 * Move an entry to a new calendar day while preserving the wall-clock
 * time-of-day and the exact duration (so a midnight-crossing entry stays
 * midnight-crossing). Returns `null` for a malformed YYYY-MM-DD string.
 *
 * Used by the recap row's 📅 date picker: changing the day shifts both
 * endpoints together; `durationSeconds` is unaffected and is not
 * recomputed by the caller.
 */
export function shiftEntryToDate(
  start: Date,
  end: Date,
  newDateISO: string,
): { startTime: Date; endTime: Date } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(newDateISO)) return null;
  const [y, mo, d] = newDateISO.split('-').map(Number);
  const startTime = new Date(start);
  startTime.setFullYear(y, mo - 1, d);
  if (
    startTime.getFullYear() !== y ||
    startTime.getMonth() !== mo - 1 ||
    startTime.getDate() !== d
  ) {
    return null;
  }
  const durationMs = end.getTime() - start.getTime();
  const endTime = new Date(startTime.getTime() + durationMs);
  return { startTime, endTime };
}

/**
 * Fields for an `addDoc` that duplicates an existing entry as an
 * identical copy (same project, designation, description, tags, billable
 * and the SAME start/end/duration). `createdAt`/`updatedAt` are added by
 * the caller with `serverTimestamp()`. `source` is reset to `'manual'`
 * (the copy is a user action, not a timer save).
 */
export function buildDuplicatePayload(entry: TimeEntry): {
  userId: string;
  projectId: string;
  designationId: string;
  description: string | null;
  tags: string[];
  startTime: TimeEntry['startTime'];
  endTime: TimeEntry['endTime'];
  durationSeconds: number;
  billable: boolean;
  source: 'manual';
} {
  return {
    userId: entry.userId,
    projectId: entry.projectId,
    designationId: entry.designationId,
    description: entry.description ?? null,
    tags: entry.tags ?? [],
    startTime: entry.startTime,
    endTime: entry.endTime,
    durationSeconds: entry.durationSeconds,
    billable: entry.billable,
    source: 'manual',
  };
}
